import { memo } from 'react';
import { ArrowDown, ArrowUp, BookCopy, Ellipsis, Eye, EyeOff, RefreshCw, Trash2 } from 'lucide-react';

import { useFormControls } from 'components/storefront/StorefrontConfigEditor/providers/FormStateProvider';
import { CAROUSEL_TYPES_WITH_VISIBILITY, SLICE_REGISTRY } from 'components/storefront/utils/constants';
import { Button } from 'shadcn/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'shadcn/components/ui/Card';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from 'shadcn/components/ui/DropdownMenu';
import { cn } from 'shadcn/lib/utils';

/**
 * A wrapper component for displaying and managing individual slices in the storefront configuration editor.
 * This component provides a card interface with options to manipulate the slice (move, duplicate, delete).
 *
 * The component is wrapped in React.memo to optimize performance by preventing unnecessary re-renders.
 * This is particularly important because SliceCardWrapper components are rendered in a list, where
 * changes to one slice (like moving or updating) shouldn't cause other slice components to re-render
 * if their props haven't changed. The memo wrapper ensures the component only re-renders if its 'index'
 * or 'slice' props have actually changed.
 *
 * @param {Object} props - Component props
 * @param {number} props.index - The position index of the slice in the list
 * @param {Object} props.slice - The slice data object
 * @param {string} props.slice.id - Unique identifier for the slice
 * @param {string} props.slice.displayType - Type of the slice, used to determine which component to render
 * @param {string} [props.slice.name] - Optional display name for the slice
 * @returns {JSX.Element} A card component containing the slice content and controls
 */
const SliceCardWrapper = memo(({ index, slice }) => {
    const {
        canSwitchSliceType,
        deleteSlice,
        duplicateSlice,
        highlightedSliceId,
        loadingServerData,
        moveSlice,
        numSlices,
        sliceCardRefs,
        toggleDuplicateSlicesDialog,
        toggleSwitchSliceTypeDialog,
        updateSlice,
    } = useFormControls();

    const SliceComponent = SLICE_REGISTRY[slice.displayType];

    return (
        <Card
            className={cn(highlightedSliceId === slice.id && 'outline outline-ring shadow-md')}
            ref={(el) => (sliceCardRefs.current[slice.id] = el)}
        >
            <CardHeader>
                <CardTitle className="flex justify-between gap-10">
                    <p>
                        {slice.name ? (
                            <>
                                {slice.name}
                                <span className="ml-2 text-sm text-muted-foreground">
                                    ({SliceComponent.displayName})
                                </span>
                            </>
                        ) : (
                            SliceComponent.displayName
                        )}
                        {!!slice.carouselID && (
                            <span className="ml-2 font-normal text-sm text-muted-foreground">
                                ID: {slice.carouselID}
                            </span>
                        )}
                    </p>
                    <div className="flex items-center gap-1">
                        {CAROUSEL_TYPES_WITH_VISIBILITY.includes(slice.displayType) && (
                            <VisibilityToggleButton
                                isHidden={slice.isHidden}
                                disabled={loadingServerData}
                                onToggle={() => updateSlice(index, { isHidden: !slice.isHidden })}
                            />
                        )}
                        <OptionsDropdown
                            disabled={loadingServerData}
                            index={index}
                            numSlices={numSlices}
                            onMoveDown={() => moveSlice(index, index + 1)}
                            onMoveUp={() => moveSlice(index, index - 1)}
                            onDuplicateInPlace={() => duplicateSlice(index)}
                            onDuplicateToConfigurations={() => toggleDuplicateSlicesDialog(slice)}
                            onSwitchSliceType={
                                canSwitchSliceType(slice.displayType)
                                    ? () => toggleSwitchSliceTypeDialog(slice, index)
                                    : undefined
                            }
                            onDelete={() => deleteSlice(index)}
                        />
                    </div>
                </CardTitle>
                <CardDescription>{SliceComponent?.description}</CardDescription>
            </CardHeader>
            <CardContent>
                <SliceComponent
                    disabled={loadingServerData}
                    index={index}
                    onChange={(updatedData) => updateSlice(index, updatedData)}
                    slice={slice}
                />
            </CardContent>
        </Card>
    );
});

/**
 * Button component for toggling slice visibility in the storefront.
 * Shows an eye icon when visible, eye-off icon when hidden, with appropriate styling.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isHidden - Whether the slice is currently hidden
 * @param {boolean} props.disabled - Whether the button is disabled
 * @param {Function} props.onToggle - Callback function to toggle visibility
 * @returns {JSX.Element} A button with eye icon for toggling visibility
 */
function VisibilityToggleButton({ disabled, isHidden, onToggle }) {
    return (
        <Button
            className={cn(isHidden ? 'text-red-500' : 'text-green-500')}
            disabled={disabled}
            onClick={() => {
                const action = isHidden ? 'show' : 'hide';
                if (window.confirm(`Are you sure you want to ${action} this slice in the Storefront?`)) {
                    onToggle();
                }
            }}
            size="icon"
            variant="ghost"
            title={
                isHidden
                    ? 'This slice is HIDDEN in the Storefront. Click to show it in the Storefront.'
                    : 'This slice is SHOWN in the Storefront. Click to hide it in the Storefront.'
            }
        >
            {isHidden ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </Button>
    );
}

/**
 * Dropdown menu component for slice operations like moving, duplicating, and deleting.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.disabled - Whether the dropdown is disabled
 * @param {number} props.index - Current index of the slice in the list
 * @param {number} props.numSlices - Total number of slices
 * @param {Function} props.onMoveDown - Callback to move the slice down in order
 * @param {Function} props.onMoveUp - Callback to move the slice up in order
 * @param {Function} props.onDuplicateInPlace - Callback to create a copy of the slice in-place
 * @param {Function} props.onDuplicateToConfigurations - Callback to create a copy of the slice to other configurations
 * @param {Function} props.onSwitchSliceType - Callback to open the switch slice type dialog
 * @param {Function} props.onDelete - Callback to remove the slice
 * @returns {JSX.Element} A dropdown menu with slice operation options
 */
function OptionsDropdown({
    disabled,
    index,
    numSlices,
    onDelete,
    onDuplicateInPlace,
    onDuplicateToConfigurations,
    onMoveDown,
    onMoveUp,
    onSwitchSliceType,
}) {
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                    disabled={disabled}
                    variant="ghost"
                    size="icon"
                    className="data-[state=open]:bg-muted data-[state=open]:hover:bg-muted"
                >
                    <Ellipsis />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
                <DropdownMenuItem disabled={index === 0} onClick={onMoveUp}>
                    <ArrowUp />
                    Move up
                </DropdownMenuItem>
                <DropdownMenuItem disabled={index === numSlices - 1} onClick={onMoveDown}>
                    <ArrowDown />
                    Move down
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onDuplicateInPlace}>
                    <BookCopy />
                    Duplicate in-place
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onDuplicateToConfigurations}>
                    <BookCopy />
                    Duplicate to configurations
                </DropdownMenuItem>
                {onSwitchSliceType && (
                    <DropdownMenuItem onClick={onSwitchSliceType}>
                        <RefreshCw />
                        Switch slice type
                    </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem variant="destructive" onClick={onDelete}>
                    <Trash2 />
                    Remove
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

export default SliceCardWrapper;
